import { differenceInMinutes, isAfter, parseISO, setHours, setMinutes } from 'date-fns';
import { PresenceStatus } from '../models/booking-overview.model';
import { FlatIndex, itemsForFlat } from './flat-index';

/**
 * A jelenlét-számításhoz szükséges eszköz-mezők (az API Device resource-a).
 * A szabályok a korábbi BookingPresenceService / PresenceWarningService logikájának betűhű átemelései
 * (spec 6.1) — a lakásonkénti lekérés helyett egy lakás-indexből dolgoznak.
 */
export interface PresenceDevice {
  readonly status: string | null;
  readonly status_changed_at: string | null;
  readonly deviceType?: { readonly id?: number } | null;
}

export interface PresenceWarningRow {
  readonly isCheckedIn: boolean;
  readonly flatInfos: readonly { readonly id: number }[];
}

const PRESENCE_STATUSES = ['occupied', 'present', 'detected', '1', 'true', 'on'];

export const isPresenceDetected = (status: string): boolean => PRESENCE_STATUSES.includes(status.toLowerCase());

/** A sor lakásai; a 0-s helykitöltő lakás is benne marad (rá nincs eszköz). */
export const presenceFlatIds = (flatInfos: readonly { readonly id: number }[]): number[] =>
  flatInfos.map((flatInfo) => flatInfo.id).filter((id) => id !== undefined);

/** Bármelyik eszköz jelez → foglalt; az idő és a státusz a legutoljára változott eszközé. */
export function aggregatePresenceStatus(devices: readonly PresenceDevice[], now: Date): PresenceStatus | undefined {
  const validDevices = devices.filter((device) => device.status !== null && device.status_changed_at !== null);
  if (validDevices.length === 0) {
    return undefined;
  }
  const mostRecent = [...validDevices].sort(
    (a, b) => parseISO(b.status_changed_at!).getTime() - parseISO(a.status_changed_at!).getTime()
  )[0];
  return {
    isOccupied: validDevices.some((device) => !!device.status && isPresenceDetected(device.status)),
    lastChanged: mostRecent.status_changed_at!,
    minutesSinceChange: differenceInMinutes(now, parseISO(mostRecent.status_changed_at!)),
    deviceStatus: mostRecent.status!
  };
}

export const presenceStatusForFlats = (
  flatIds: readonly number[],
  index: FlatIndex<PresenceDevice>,
  now: Date
): PresenceStatus | undefined => aggregatePresenceStatus(flatIds.flatMap((flatId) => itemsForFlat(index, flatId)), now);

/** Érkezőnél: a mai küszöbóra után érzékelt jelenlét bármelyik (nem 0-s) lakásban, amíg nincs bejelentkezve. */
export function hasPresenceWarning(
  row: PresenceWarningRow,
  index: FlatIndex<PresenceDevice>,
  now: Date,
  thresholdHour: number,
  presenceTypeId: number
): boolean {
  if (row.isCheckedIn || !row.flatInfos.length) {
    return false;
  }
  const thresholdTime = setMinutes(setHours(now, thresholdHour), 0);
  return row.flatInfos.some(
    (flatInfo) => !!flatInfo.id && hasPresenceAfter(itemsForFlat(index, flatInfo.id), thresholdTime, presenceTypeId)
  );
}

function hasPresenceAfter(devices: readonly PresenceDevice[], thresholdTime: Date, presenceTypeId: number): boolean {
  return devices
    .filter((device) => device.deviceType?.id === presenceTypeId)
    .some(
      (device) =>
        !!device.status &&
        !!device.status_changed_at &&
        isAfter(parseISO(device.status_changed_at), thresholdTime) &&
        isPresenceDetected(device.status)
    );
}
